import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { ChevronLeft, Link2, Loader2, Package2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { packingApiService, BoxType, EndgoodBoxType } from '../services/packingApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';

const BoxMasterEndgoodsPage: React.FC = () => {
  const navigate = useNavigate();
  const { boxTypeId: boxTypeIdParam } = useParams<{ boxTypeId: string }>();
  const boxTypeId = boxTypeIdParam ? parseInt(boxTypeIdParam, 10) : NaN;

  const [boxType, setBoxType] = useState<BoxType | null>(null);
  const [links, setLinks] = useState<EndgoodBoxType[]>([]);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newLink, setNewLink] = useState({
    endgoodId: '',
    minQuantity: '1',
    maxQuantity: '100',
    priority: '0',
    isDefault: false,
    isActive: true,
  });

  const endgoodNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const eg of endgoods) {
      m.set(eg.id, eg.endgoodName);
    }
    return m;
  }, [endgoods]);

  const linkedEndgoodIds = useMemo(() => new Set(links.map((l) => l.endgoodId)), [links]);

  const unlinkedEndgoods = useMemo(
    () => endgoods.filter((eg) => !linkedEndgoodIds.has(eg.id)),
    [endgoods, linkedEndgoodIds]
  );

  const loadAll = useCallback(async () => {
    if (!Number.isFinite(boxTypeId) || boxTypeId <= 0) {
      setBoxType(null);
      setLinks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [boxRes, linksRes, egRes] = await Promise.all([
        packingApiService.getBoxTypeById(String(boxTypeId)),
        packingApiService.getEndgoodBoxTypes({ boxTypeId }),
        endgoodApiService.getEndgoodsIdName(),
      ]);

      setBoxType(boxRes.success && boxRes.data ? boxRes.data : null);

      let linkRows: EndgoodBoxType[] = [];
      if (linksRes.success && Array.isArray(linksRes.data)) {
        linkRows = linksRes.data.filter((row) => Number(row.boxTypeId) === boxTypeId);
      }
      setLinks(linkRows);

      setEndgoods(egRes.success && Array.isArray(egRes.data) ? egRes.data : []);
    } catch {
      setBoxType(null);
      setLinks([]);
      setEndgoods([]);
    } finally {
      setLoading(false);
    }
  }, [boxTypeId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleCreateLink = async () => {
    if (!Number.isFinite(boxTypeId) || boxTypeId <= 0) return;
    const endgoodId = Number(newLink.endgoodId);
    if (!Number.isFinite(endgoodId) || endgoodId <= 0) {
      toast.error('Please select an endgood');
      return;
    }
    if (linkedEndgoodIds.has(endgoodId)) {
      toast.error('This endgood is already linked to this box type');
      return;
    }
    const minQuantity = Number(newLink.minQuantity);
    const maxQuantity = Number(newLink.maxQuantity);
    if (!Number.isFinite(minQuantity) || minQuantity < 0) {
      toast.error('Min quantity must be a valid number');
      return;
    }
    if (!Number.isFinite(maxQuantity) || maxQuantity < 0) {
      toast.error('Max quantity must be a valid number');
      return;
    }
    if (minQuantity > maxQuantity) {
      toast.error('Min quantity cannot be greater than max quantity');
      return;
    }

    try {
      setSaving(true);
      await packingApiService.createEndgoodBoxType({
        endgoodId,
        boxTypeId,
        minQuantity,
        maxQuantity,
        isDefault: newLink.isDefault,
        isActive: newLink.isActive,
        priority: Number(newLink.priority) || 0,
      });
      setNewLink({
        endgoodId: '',
        minQuantity: '1',
        maxQuantity: '100',
        priority: '0',
        isDefault: false,
        isActive: true,
      });
      await loadAll();
    } catch {
      // service toast
    } finally {
      setSaving(false);
    }
  };

  const formatNum = (v?: number | null) => {
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  if (!Number.isFinite(boxTypeId) || boxTypeId <= 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Invalid box type.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/packing-entries/box-master')}>
          Back to Box Master
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6 max-w-5xl mx-auto">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Link2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Endgood links</CardTitle>
                  <p className="text-sm text-primary-100 mt-1">
                    {loading ? (
                      'Loading…'
                    ) : boxType ? (
                      <>
                        Box type: <span className="font-semibold text-white">{boxType.name}</span>
                        <span className="text-primary-100"> (ID {boxType.id})</span>
                      </>
                    ) : (
                      'Box type not found'
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
                  onClick={() => navigate('/packing-entries/box-master')}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Box Master
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/30 bg-white text-primary-700 hover:bg-primary-50"
                  onClick={() => void loadAll()}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Linked endgoods</CardTitle>
            <p className="text-sm text-muted-foreground">
              Relationships for this box type: min/max quantity per endgood, priority, and default flag.
            </p>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : links.length === 0 ? (
              <p className="text-sm text-center text-muted-foreground py-8">No endgoods linked to this box type yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead className="text-right">Endgood ID</TableHead>
                      <TableHead>Endgood</TableHead>
                      <TableHead className="text-right">Min qty</TableHead>
                      <TableHead className="text-right">Max qty</TableHead>
                      <TableHead className="text-right">Priority</TableHead>
                      <TableHead className="text-center">Default</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead className="text-right">Link ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {links.map((row) => (
                      <TableRow key={row.id} className="hover:bg-primary-50/40">
                        <TableCell className="text-right">{row.endgoodId}</TableCell>
                        <TableCell className="font-medium">
                          {row.endgood?.endgoodName ?? endgoodNameById.get(row.endgoodId) ?? '—'}
                        </TableCell>
                        <TableCell className="text-right">{formatNum(row.minQuantity)}</TableCell>
                        <TableCell className="text-right">{formatNum(row.maxQuantity)}</TableCell>
                        <TableCell className="text-right">{formatNum(row.priority)}</TableCell>
                        <TableCell className="text-center">{row.isDefault ? 'Yes' : 'No'}</TableCell>
                        <TableCell className="text-center">{row.isActive ? 'Yes' : 'No'}</TableCell>
                        <TableCell className="text-right">{row.id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800 flex items-center gap-2">
              <Package2 className="h-5 w-5 text-primary-700" />
              Add link
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Create a new endgood–box relationship when one does not already exist for this box type.
            </p>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : unlinkedEndgoods.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                {endgoods.length === 0
                  ? 'Could not load endgoods list.'
                  : 'Every loaded endgood already has a link for this box type (or none remain to add).'}
              </p>
            ) : (
              <div className="grid gap-4 max-w-xl">
                <div className="space-y-2">
                  <Label htmlFor="new-link-endgood">Endgood</Label>
                  <Select
                    value={newLink.endgoodId}
                    onValueChange={(v) => setNewLink((p) => ({ ...p, endgoodId: v }))}
                  >
                    <SelectTrigger id="new-link-endgood" className="border-primary-200">
                      <SelectValue placeholder="Select endgood" />
                    </SelectTrigger>
                    <SelectContent>
                      {unlinkedEndgoods.map((eg) => (
                        <SelectItem key={eg.id} value={String(eg.id)}>
                          {eg.endgoodName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-link-min">Min quantity</Label>
                    <Input
                      id="new-link-min"
                      type="number"
                      className="border-primary-200"
                      value={newLink.minQuantity}
                      onChange={(e) => setNewLink((p) => ({ ...p, minQuantity: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-link-max">Max quantity</Label>
                    <Input
                      id="new-link-max"
                      type="number"
                      className="border-primary-200"
                      value={newLink.maxQuantity}
                      onChange={(e) => setNewLink((p) => ({ ...p, maxQuantity: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-link-priority">Priority</Label>
                  <Input
                    id="new-link-priority"
                    type="number"
                    className="border-primary-200"
                    value={newLink.priority}
                    onChange={(e) => setNewLink((p) => ({ ...p, priority: e.target.value }))}
                  />
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={newLink.isDefault}
                      onCheckedChange={(checked) => setNewLink((p) => ({ ...p, isDefault: checked }))}
                    />
                    <span className="text-sm text-gray-700">Default for endgood</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={newLink.isActive}
                      onCheckedChange={(checked) => setNewLink((p) => ({ ...p, isActive: checked }))}
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </div>
                </div>
                <Button
                  type="button"
                  className="bg-primary-600 hover:bg-primary-700 w-fit"
                  disabled={saving || !newLink.endgoodId}
                  onClick={() => void handleCreateLink()}
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Create link
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BoxMasterEndgoodsPage;
